import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@erp/ui';
import { Button } from '@erp/ui';

/**
 * Camera-based barcode scanner using the native BarcodeDetector API.
 *
 * Supported on Chrome / Edge / Samsung Internet (Android, Desktop Chrome).
 * iOS Safari has no support → user is shown an explanatory message and the
 * dialog falls back to keyboard entry via the picker's search input.
 */

interface BarcodeDetection {
  rawValue: string;
}

interface BarcodeDetectorInstance {
  detect(source: HTMLVideoElement): Promise<BarcodeDetection[]>;
}

interface BarcodeDetectorCtor {
  new (options?: { formats?: string[] }): BarcodeDetectorInstance;
  getSupportedFormats?: () => Promise<string[]>;
}

declare global {
  interface Window {
    BarcodeDetector?: BarcodeDetectorCtor;
  }
}

const SCAN_FORMATS = [
  'ean_13',
  'ean_8',
  'code_128',
  'code_39',
  'code_93',
  'codabar',
  'upc_a',
  'upc_e',
  'qr_code',
  'itf',
];

interface BarcodeScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onScanned: (text: string) => void;
}

export function BarcodeScannerDialog({ open, onOpenChange, onScanned }: BarcodeScannerDialogProps) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const onScannedRef = useRef(onScanned);
  onScannedRef.current = onScanned;

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    let frameId: number | null = null;
    setError(null);
    setStarting(true);

    async function start() {
      if (typeof window === 'undefined' || !window.BarcodeDetector) {
        setError(t('sales.picker.scannerUnsupported'));
        setStarting(false);
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(t('sales.picker.cameraUnsupported'));
        setStarting(false);
        return;
      }

      let detector: BarcodeDetectorInstance;
      try {
        detector = new window.BarcodeDetector({ formats: SCAN_FORMATS });
      } catch {
        // Some browsers throw if any format is unsupported — retry without filter.
        try {
          detector = new window.BarcodeDetector();
        } catch {
          setError(t('sales.picker.scannerUnsupported'));
          setStarting(false);
          return;
        }
      }

      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
      } catch (e) {
        const err = e as DOMException;
        if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
          setError(t('sales.picker.cameraDenied'));
        } else {
          setError(t('sales.picker.cameraError'));
        }
        setStarting(false);
        return;
      }

      if (cancelled) {
        stream.getTracks().forEach((tr) => tr.stop());
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        try {
          await video.play();
        } catch {
          // play() can reject if user navigates away — not fatal.
        }
      }
      setStarting(false);

      const tick = async () => {
        if (cancelled || !videoRef.current) return;
        if (videoRef.current.readyState >= 2) {
          try {
            const detections = await detector.detect(videoRef.current);
            const value = detections[0]?.rawValue;
            if (value) {
              onScannedRef.current(value);
              return;
            }
          } catch {
            // Detection errors during a frame are non-fatal — keep trying.
          }
        }
        frameId = requestAnimationFrame(tick);
      };
      frameId = requestAnimationFrame(tick);
    }

    start();

    return () => {
      cancelled = true;
      if (frameId !== null) cancelAnimationFrame(frameId);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((tr) => tr.stop());
        streamRef.current = null;
      }
      const video = videoRef.current;
      if (video) {
        video.pause();
        video.srcObject = null;
      }
    };
  }, [open, t]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!flex flex-col p-0 gap-0 overflow-hidden max-md:inset-0 max-md:top-0 max-md:start-0 max-md:translate-x-0 max-md:rtl:translate-x-0 max-md:translate-y-0 max-md:max-w-full max-md:max-h-none max-md:w-screen max-md:h-[100dvh] max-md:rounded-none md:max-w-md md:h-[520px]"
        showCloseButton={false}
      >
        <DialogHeader className="p-4 pb-2 shrink-0">
          <DialogTitle>{t('sales.picker.scanBarcode')}</DialogTitle>
          <DialogDescription>{t('sales.picker.scanHint')}</DialogDescription>
        </DialogHeader>

        <div className="relative flex-1 bg-black overflow-hidden">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />

          {/* Viewfinder overlay */}
          {!error && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-3/4 max-w-xs h-32 border-2 border-white rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]">
                <div className="absolute top-1/2 left-[12.5%] right-[12.5%] h-px bg-red-500/80" />
              </div>
            </div>
          )}

          {starting && !error && (
            <div className="absolute inset-x-0 bottom-4 text-center">
              <span className="inline-block px-3 py-1 rounded-full bg-black/60 text-white text-xs">
                {t('common.loading')}
              </span>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background p-6">
              <p className="text-center text-sm text-foreground">{error}</p>
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-muted/50 shrink-0 flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
